import { useAxios } from '../useAxios';
import {
  ISignalSettings, SignalLeadSort, SignalLeadStatus, SignalLeadStatusFilter, SignalSourceType, SignalType,
} from '../../../interfaces/models/signals';

export interface ISignalLeadsQuery {
  status?: SignalLeadStatusFilter;
  type?: SignalType | '';
  owner?: 'me' | '';
  search?: string;
  sort?: SignalLeadSort;
  page?: number;
  limit?: number;
}

export type ISignalSettingsPatch = Partial<Pick<ISignalSettings, 'mode' | 'autoApproveScore' | 'ignoreScore'>> & {
  icp?: Partial<ISignalSettings['icp']>;
};

/**
 * /signals — every call returns the whole envelope ({ success, data, message }) so the
 * caller can show the API's own message when something is refused.
 */
export const useSignalsRequest = () => {
  const { sendRequest } = useAxios();
  const route = '/signals';

  const getAccess = () =>
    sendRequest({ slug: 'signals-access', url: `${route}/access`, method: 'GET', displayToast: false, all: true });

  const getAccounts = () =>
    sendRequest({ slug: 'signals-accounts', url: `${route}/accounts`, method: 'GET', displayToast: false, all: true });

  const getSources = () =>
    sendRequest({ slug: 'signals-sources', url: `${route}/sources`, method: 'GET', displayToast: false, all: true });

  const setAccountSource = (accountId: string, type: Exclude<SignalSourceType, 'post'>, enabled: boolean) =>
    sendRequest({
      slug: `signals-source-${accountId}-${type}`,
      url: `${route}/sources/account/${encodeURIComponent(accountId)}/${type}`,
      method: 'PUT',
      payload: { enabled },
      displayToast: false,
      all: true,
    });

  const addPostSource = (accountId: string, postUrl: string) =>
    sendRequest({
      slug: 'signals-add-post',
      url: `${route}/sources/posts`,
      method: 'POST',
      payload: { accountId, postUrl },
      displayToast: false,
      all: true,
    });

  const updateSource = (id: string, enabled: boolean) =>
    sendRequest({ slug: `signals-update-source-${id}`, url: `${route}/sources/${id}`, method: 'PATCH', payload: { enabled }, displayToast: false, all: true });

  const deleteSource = (id: string) =>
    sendRequest({ slug: `signals-delete-source-${id}`, url: `${route}/sources/${id}`, method: 'DELETE', displayToast: false, all: true });

  const getLeads = (query: ISignalLeadsQuery = {}) =>
    sendRequest({
      slug: 'signals-leads',
      url: `${route}/leads`,
      method: 'GET',
      params: Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== '')),
      displayToast: false,
      all: true,
    });

  const getLeadSignals = (leadId: string) =>
    sendRequest({ slug: `signals-lead-${leadId}`, url: `${route}/leads/${leadId}/signals`, method: 'GET', displayToast: false, all: true });

  const updateLeadStatus = (ids: string[], status: SignalLeadStatus, campaignId?: string) =>
    sendRequest({
      slug: 'signals-lead-status',
      url: `${route}/leads/status`,
      method: 'POST',
      payload: { ids, status, ...(campaignId ? { campaignId } : {}) },
      displayToast: false,
      all: true,
    });

  const getSettings = () =>
    sendRequest({ slug: 'signals-settings', url: `${route}/settings`, method: 'GET', displayToast: false, all: true });

  const updateSettings = (patch: ISignalSettingsPatch) =>
    sendRequest({ slug: 'signals-settings-save', url: `${route}/settings`, method: 'PUT', payload: patch, displayToast: false, all: true });

  const setMyCampaign = (campaignId: string | null) =>
    sendRequest({ slug: 'signals-my-campaign', url: `${route}/settings/my-campaign`, method: 'PUT', payload: { campaignId }, displayToast: false, all: true });

  return {
    getSettings,
    updateSettings,
    setMyCampaign,
    getAccess,
    getAccounts,
    getSources,
    setAccountSource,
    addPostSource,
    updateSource,
    deleteSource,
    getLeads,
    getLeadSignals,
    updateLeadStatus,
  };
};
